import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import formService from '../../services/formService';
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';

const defaultSchema = {
  title: "Untitled Form",
  title_tc: "未命名表單",
  fields: [
    { key: "full_name", label: "Full Name", label_tc: "姓名", type: "text", required: true },
    { key: "email", label: "Email", label_tc: "電郵", type: "email", required: false },
    { key: "attend", label: "Will you attend?", label_tc: "你會出席嗎？", type: "select", options: ["Yes","No"], options_tc: ["是","否"], required: true },
  ]
};

const getFieldTypes = (t) => [
  { type: 'text', label: t('forms.builder.fieldTypes.text') },
  { type: 'email', label: t('forms.builder.fieldTypes.email') },
  { type: 'number', label: t('forms.builder.fieldTypes.number') },
  { type: 'date', label: t('forms.builder.fieldTypes.date') },
  { type: 'textarea', label: t('forms.builder.fieldTypes.textarea') },
  { type: 'select', label: t('forms.builder.fieldTypes.select') },
  { type: 'multiselect', label: t('forms.builder.fieldTypes.multiselect') },
  { type: 'radio', label: t('forms.builder.fieldTypes.radio') },
  { type: 'checkbox', label: t('forms.builder.fieldTypes.checkbox') },
  { type: 'file', label: t('forms.builder.fieldTypes.file') },
];

const slugify = (s) => (s || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 40);

const ensureUniqueKey = (key, fields, skipIndex=-1) => {
  let base = key || 'field';
  let k = base;
  let i = 1;
  while (fields.some((f, idx) => idx !== skipIndex && f.key === k)) {
    k = `${base}_${i++}`;
  }
  return k;
};

const SortableItem = ({ id, index, field, selected, onSelect }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} className={`border rounded p-3 bg-white cursor-move ${selected ? 'ring-2 ring-blue-400' : ''}`} onClick={() => onSelect(index)} {...attributes} {...listeners}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">
            {field.label || field.key}
            {field.label_tc && <span className="text-gray-500 ml-2">{field.label_tc}</span>}
          </div>
          <div className="text-xs text-gray-500">{field.type}{field.required ? ' • required' : ''}</div>
        </div>
        <div className="text-xs text-gray-400">⋮⋮</div>
      </div>
    </div>
  );
};

const FormBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isEdit = Boolean(id);
  const fieldTypes = getFieldTypes(t);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('draft');
  const [openAt, setOpenAt] = useState('');
  const [closeAt, setCloseAt] = useState('');
  const [schemaObj, setSchemaObj] = useState(defaultSchema);
  const [activeTab, setActiveTab] = useState('builder');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  useEffect(() => {
    const load = async () => {
      if (!isEdit) return;
      try {
        const f = await formService.getById(id);
        setName(f.name);
        setSlug(f.slug);
        setDescription(f.description || '');
        setStatus(f.status || 'draft');
        setOpenAt(f.open_at ? f.open_at.substring(0,16) : '');
        setCloseAt(f.close_at ? f.close_at.substring(0,16) : '');
        setSchemaObj(f.schema || defaultSchema);
      } catch (e) {
        setError('Failed to load form');
      }
    };
    load();
  }, [id, isEdit]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name, slug, description, status,
        open_at: openAt ? new Date(openAt).toISOString() : null,
        close_at: closeAt ? new Date(closeAt).toISOString() : null,
        schema: schemaObj
      };
      if (isEdit) {
        await formService.update(id, payload);
      } else {
        await formService.create(payload);
      }
      navigate('/forms');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const schemaJSON = useMemo(() => JSON.stringify(schemaObj, null, 2), [schemaObj]);

  const addField = (type) => {
    const df = { type, label: `${type[0].toUpperCase()}${type.slice(1)} field`, label_tc: '', key: '' };
    const key = ensureUniqueKey(slugify(df.label), schemaObj.fields);
    const field = { ...df, key };
    setSchemaObj(prev => ({ ...prev, fields: [...(prev.fields || []), field] }));
    setSelectedIndex((schemaObj.fields || []).length);
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = (schemaObj.fields || []).map((_, idx) => `${idx}`);
    const from = ids.indexOf(active.id);
    const to = ids.indexOf(over.id);
    if (from === -1 || to === -1) return;
    setSchemaObj(prev => ({ ...prev, fields: arrayMove(prev.fields, from, to) }));
    setSelectedIndex(to);
  };

  const updateField = (idx, patch) => {
    setSchemaObj(prev => {
      const fields = [...(prev.fields || [])];
      const next = { ...fields[idx], ...patch };
      if (patch.label !== undefined && (patch.key === undefined || next.key === '' || next.key === slugify(fields[idx]?.label || '') )) {
        const base = slugify(patch.label);
        next.key = ensureUniqueKey(base, fields, idx);
      }
      if (patch.key !== undefined) {
        next.key = ensureUniqueKey(slugify(patch.key), fields, idx);
      }
      fields[idx] = next;
      return { ...prev, fields };
    });
  };

  const removeField = (idx) => {
    setSchemaObj(prev => {
      const fields = [...(prev.fields || [])];
      fields.splice(idx, 1);
      return { ...prev, fields };
    });
    setSelectedIndex(-1);
  };

  const selectedField = selectedIndex >= 0 ? (schemaObj.fields || [])[selectedIndex] : null;

  return (
    <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">{isEdit ? t('forms.builder.editTitle') : t('forms.builder.createTitle')}</h2>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{t('forms.builder.errorLoading')}</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Form Card */}
      <div className="bg-white shadow-sm rounded-lg p-6 space-y-6">
        {/* Basic form meta */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
            <label className="block text-sm font-medium text-gray-700">{t('forms.builder.labels.name')}</label>
            <input className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" value={name} onChange={(e)=>setName(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('forms.builder.labels.slug')}</label>
            <input className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed" value={slug} onChange={(e)=>setSlug(e.target.value)} disabled={isEdit} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">{t('forms.builder.labels.description')}</label>
            <textarea className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" rows={2} value={description} onChange={(e)=>setDescription(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('forms.builder.labels.status')}</label>
            <select className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" value={status} onChange={(e)=>setStatus(e.target.value)}>
              <option value="draft">{t('forms.builder.statusOptions.draft')}</option>
              <option value="published">{t('forms.builder.statusOptions.published')}</option>
              <option value="archived">{t('forms.builder.statusOptions.archived')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('forms.builder.labels.openAt')}</label>
            <input type="datetime-local" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" value={openAt} onChange={(e)=>setOpenAt(e.target.value)} />
            <p className="mt-1 text-xs text-gray-500">{t('forms.builder.placeholders.openAtNote')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('forms.builder.labels.closeAt')}</label>
            <input type="datetime-local" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500" value={closeAt} onChange={(e)=>setCloseAt(e.target.value)} />
            <p className="mt-1 text-xs text-gray-500">{t('forms.builder.placeholders.closeAtNote')}</p>
          </div>
        </div>
        {/* Full-width builder section */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex border-b border-gray-200 mb-4">
            <button type="button" className={`px-4 py-2 text-sm font-medium ${activeTab==='builder' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={()=>setActiveTab('builder')}>{t('forms.builder.tabs.builder')}</button>
            <button type="button" className={`px-4 py-2 text-sm font-medium ${activeTab==='json' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={()=>setActiveTab('json')}>{t('forms.builder.tabs.json')}</button>
          </div>

          {activeTab === 'builder' && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {/* Palette */}
              <div className="lg:col-span-1">
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="text-sm font-semibold text-gray-700 mb-3">{t('forms.builder.palette.title')}</div>
                  <div className="grid grid-cols-2 gap-2">
                    {fieldTypes.map((ft) => (
                      <button key={ft.type} type="button" className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50" onClick={() => addField(ft.type)}>{ft.label}</button>
                    ))}
                  </div>
                  <div className="mt-4 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('forms.builder.palette.formTitleEn')}</label>
                      <input className="mt-1 block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={schemaObj.title || ''} onChange={(e)=>setSchemaObj((prev)=>({ ...prev, title: e.target.value }))} placeholder="English title" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('forms.builder.palette.formTitleTc')}</label>
                      <input className="mt-1 block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={schemaObj.title_tc || ''} onChange={(e)=>setSchemaObj((prev)=>({ ...prev, title_tc: e.target.value }))} placeholder="中文標題" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Canvas */}
              <div className="lg:col-span-2">
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="text-sm text-gray-600 mb-3">{t('forms.builder.canvas.dragInstruction')}</div>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={(schemaObj.fields || []).map((_, idx) => `${idx}`)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {(schemaObj.fields || []).length === 0 && (
                          <div className="text-sm text-gray-500 text-center py-8">{t('forms.builder.canvas.empty')}</div>
                        )}
                        {(schemaObj.fields || []).map((f, idx) => (
                          <SortableItem key={`${idx}`} id={`${idx}`} index={idx} field={f} selected={selectedIndex === idx} onSelect={setSelectedIndex} />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              </div>

              {/* Inspector */}
              <div className="lg:col-span-1">
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="text-sm font-semibold text-gray-700 mb-3">{t('forms.builder.inspector.title')}</div>
                  {!selectedField && <div className="text-sm text-gray-500 text-center py-8">{t('forms.builder.inspector.empty')}</div>}
                  {selectedField && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700">{t('forms.builder.inspector.labels.labelEn')}</label>
                        <input className="mt-1 block w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500" value={selectedField.label || ''} onChange={(e)=>updateField(selectedIndex, { label: e.target.value })} placeholder="English label" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">{t('forms.builder.inspector.labels.labelTc')}</label>
                        <input className="mt-1 block w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500" value={selectedField.label_tc || ''} onChange={(e)=>updateField(selectedIndex, { label_tc: e.target.value })} placeholder="中文標籤" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">{t('forms.builder.inspector.labels.key')}</label>
                        <input className="mt-1 block w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500" value={selectedField.key || ''} onChange={(e)=>updateField(selectedIndex, { key: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">{t('forms.builder.inspector.labels.type')}</label>
                        <select className="mt-1 block w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500" value={selectedField.type} onChange={(e)=>updateField(selectedIndex, { type: e.target.value })}>
                          {fieldTypes.map((ft) => (
                            <option key={ft.type} value={ft.type}>{ft.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <input id="req" type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" checked={!!selectedField.required} onChange={(e)=>updateField(selectedIndex, { required: e.target.checked })} />
                        <label htmlFor="req" className="text-sm text-gray-700">{t('forms.builder.inspector.labels.required')}</label>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">{t('forms.builder.inspector.labels.placeholder')}</label>
                        <input className="mt-1 block w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500" value={selectedField.placeholder || ''} onChange={(e)=>updateField(selectedIndex, { placeholder: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700">{t('forms.builder.inspector.labels.placeholderTc')}</label>
                        <input className="mt-1 block w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500" value={selectedField.placeholder_tc || ''} onChange={(e)=>updateField(selectedIndex, { placeholder_tc: e.target.value })} placeholder="中文預設提示" />
                      </div>
                      {(selectedField.type === 'select' || selectedField.type === 'multiselect' || selectedField.type === 'radio') && (
                        <>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">{t('forms.builder.inspector.labels.optionsEn')}</label>
                            <input className="mt-1 block w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500" value={(selectedField.options || []).join(', ')} onChange={(e)=>updateField(selectedIndex, { options: e.target.value.split(',').map((s)=>s.trim()).filter(Boolean) })} placeholder="Yes, No, Maybe" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700">{t('forms.builder.inspector.labels.optionsTc')}</label>
                            <input className="mt-1 block w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500" value={(selectedField.options_tc || []).join(', ')} onChange={(e)=>updateField(selectedIndex, { options_tc: e.target.value.split(',').map((s)=>s.trim()).filter(Boolean) })} placeholder="是, 否, 或許" />
                          </div>
                        </>
                      )}
                      <div className="pt-2">
                        <button type="button" className="w-full px-3 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded" onClick={() => removeField(selectedIndex)}>{t('forms.builder.inspector.removeField')}</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'json' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('forms.builder.tabs.json')}</label>
              <textarea className="block w-full px-3 py-2 font-mono text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" rows={16} value={schemaJSON} onChange={(e)=>{
                try {
                  const parsed = JSON.parse(e.target.value);
                  setSchemaObj(parsed);
                  setError(null);
                } catch (err) {
                  setError(t('forms.builder.invalidJson'));
                }
              }} />
              <p className="mt-2 text-xs text-gray-500">{t('forms.builder.jsonNote')}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
          <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md" onClick={()=>navigate('/forms')}>
            {t('forms.builder.buttons.cancel')}
          </button>
          <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed" onClick={handleSave} disabled={saving}>
            {saving ? t('forms.builder.buttons.saving') : t('forms.builder.buttons.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FormBuilder;
